import { Immutable } from "immer";
import React from "react";
import { hasRichTextContent } from "tsi-common-react/src/utils/streamfieldContent";
import { t } from "ttag";

import type {
    ImageChooserBlock,
    SharedCompetitionCompareTableFeature,
    SealyCompetitionCompareTableBlock as Value,
} from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type IProps = Immutable<{
    value: Value;
}>;

type IFeature = Immutable<SharedCompetitionCompareTableFeature>;
type IImage = Immutable<ImageChooserBlock>;

/**
 * Sealy's competition-compare table, ported off the shared
 * `competition-compare-table-block.html` (which sealy formerly rendered via the
 * inherited `Meta.template`). Sealy uses the IMAGE-logo + inline-CTA variant of
 * that markup (same as cocoon): `logo` is an `ImageChooserBlock` (never
 * richtext, so the `logo_is_richtext` branch is dead) and there are no
 * `competitor_cta_*` fields (so `use_separate_cta_row` is always false and the
 * CTA renders inline in the last "our" cell). The sealy-specific addition is
 * the `highlight--{our_column_background_color}` class on the `<table>`,
 * matching the markup's `{% if value.our_column_background_color %}` guard
 * (omitted entirely when null).
 *
 * The markup used Wagtail `{% image width-150/width-50 %}` renditions, but the
 * block's `ImageChooserBlock` fields serialize only the default rendition set
 * (`original`, `thumbnail`), so we render the original `url` directly. This is
 * visually equivalent: the SCSS CSS-sizes the logo (`max-width: 100%`) and the
 * status icons (1.75rem square), so the source resolution is moot.
 */

const StatusIcon = (
    props: Immutable<{ icon: IImage | null; alt: string }>,
): React.JSX.Element | null => {
    if (!props.icon) {
        return null;
    }
    return (
        <img
            src={props.icon.url}
            alt={props.alt}
            loading="lazy"
            decoding="async"
        />
    );
};

const OurOrCompetitorCell = (
    props: Immutable<{
        status: "yes" | "no" | "text" | "" | null | undefined;
        text: string | null | undefined;
        affirmativeIcon: IImage | null;
        negativeIcon: IImage | null;
        cta?: React.ReactNode;
    }>,
): React.JSX.Element | null => {
    if (props.status === "yes") {
        return (
            <td>
                {!props.text ? (
                    <StatusIcon
                        icon={props.affirmativeIcon}
                        alt={t`We have it`}
                    />
                ) : (
                    <div>
                        <StatusIcon
                            icon={props.affirmativeIcon}
                            alt={t`We have it`}
                        />
                        {props.text}
                    </div>
                )}
                {props.cta}
            </td>
        );
    }
    if (props.status === "no") {
        return (
            <td>
                {!props.text ? (
                    <StatusIcon
                        icon={props.negativeIcon}
                        alt={t`We don't have it`}
                    />
                ) : (
                    <div>
                        <StatusIcon
                            icon={props.negativeIcon}
                            alt={t`We don't have it`}
                        />
                        {props.text}
                    </div>
                )}
            </td>
        );
    }
    if (props.status === "text") {
        return (
            <td>
                {props.text}
                {props.cta}
            </td>
        );
    }
    return null;
};

export const SealyCompetitionCompareTableBlock = (
    props: IProps,
): React.JSX.Element => {
    const v = props.value;
    const features = v.features ?? [];
    const hasSecondCompetitor = hasRichTextContent(v.second_competitor_name);

    const renderFeatureRow = (
        feature: IFeature,
        index: number,
    ): React.JSX.Element => {
        const isLast = index === features.length - 1;
        // Sealy never uses a separate CTA row (no competitor_cta_* fields), so
        // the last row is always `last no-cta` and the CTA renders inline in
        // the last "our" cell (matching the markup's `forloop.last` branch).
        const cta =
            isLast && v.cta_destination ? (
                <a className="button" href={v.cta_destination.url}>
                    {v.cta_text}
                </a>
            ) : null;
        return (
            <tr key={index} className={isLast ? "last no-cta" : ""}>
                <th scope="row">{feature.feature_name}</th>
                <OurOrCompetitorCell
                    status={feature.our_feature_status}
                    text={feature.our_feature_text}
                    affirmativeIcon={v.our_affirmative_icon}
                    negativeIcon={v.negative_icon}
                    cta={cta}
                />
                <OurOrCompetitorCell
                    status={feature.competition_feature_status}
                    text={feature.competition_feature_text}
                    affirmativeIcon={v.affirmative_icon}
                    negativeIcon={v.negative_icon}
                />
                {hasSecondCompetitor &&
                    (feature.second_competition_feature_status === "yes" ? (
                        <td>
                            <StatusIcon
                                icon={v.affirmative_icon}
                                alt={t`We have it`}
                            />
                        </td>
                    ) : feature.second_competition_feature_status === "no" ? (
                        <td>
                            <StatusIcon
                                icon={v.negative_icon}
                                alt={t`We don't have it`}
                            />
                        </td>
                    ) : feature.second_competition_feature_status === "text" ? (
                        <td>{feature.second_competition_feature_text}</td>
                    ) : null)}
            </tr>
        );
    };

    return (
        <section
            className={concatClassNames([
                "competition-compare-table-block",
                v.background_style?.style ?? "",
                v.responsive_visibility?.visibility ?? "",
                v.theme ?? "",
                hasSecondCompetitor
                    ? "competition-compare-table-block--four-columns"
                    : "",
                v.cta_destination
                    ? "competition-compare-table-block--with-cta"
                    : "",
            ])}
        >
            <table
                className={concatClassNames([
                    v.our_column_background_color
                        ? `highlight--${v.our_column_background_color}`
                        : "",
                ])}
            >
                <thead>
                    <tr>
                        <th scope="col">
                            <p className="ada-screenreader-only">{t`Features`}</p>
                        </th>
                        <th scope="col">
                            {v.logo && (
                                <img
                                    src={v.logo.url}
                                    alt={v.logo.default_alt_text}
                                    loading="lazy"
                                    decoding="async"
                                />
                            )}
                        </th>
                        <th scope="col">
                            <RichTextBlock
                                className="rich-text"
                                value={v.competitor_name ?? ""}
                            />
                        </th>
                        {hasSecondCompetitor && (
                            <th scope="col">
                                <RichTextBlock
                                    className="rich-text"
                                    value={v.second_competitor_name ?? ""}
                                />
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>{features.map(renderFeatureRow)}</tbody>
            </table>
            {v.fineprint && (
                <div className="fineprint">
                    <RichTextBlock className="rich-text" value={v.fineprint} />
                </div>
            )}
        </section>
    );
};
